//----------------------------------------------------------------------------------------------------------------------
// LOD (Level of Detail) Property Handler
//----------------------------------------------------------------------------------------------------------------------

import { Mesh } from '@babylonjs/core';

import type { LevelManager } from '../managers/level.ts';
import { SAGELogger } from '../utils/logger.ts';

//----------------------------------------------------------------------------------------------------------------------

const logger = new SAGELogger('LodHandler');

/**
 * Parses a comma-separated string of distances into an array of numbers.
 * Filters out invalid (NaN) values.
 */
function parseDistances(distancesStr : string) : number[]
{
    return distancesStr
        .split(',')
        .map((str) => parseFloat(str.trim()))
        .filter((num) => !isNaN(num));
}

/**
 * Register the LOD (Level of Detail) property handler.
 *
 * Supports the following property on scene nodes:
 * - `lod_distances` (string): Comma-separated distances for LOD levels
 *
 * The mesh's children are used as LOD levels in order.
 * Example: "10,25,50" means:
 * - 0-10 units: use first child mesh
 * - 10-25 units: use second child mesh
 * - 25-50 units: use third child mesh
 * - 50+ units: use null (not visible)
 *
 * @param levelManager
 */
export function registerLodHandler(levelManager : LevelManager) : void
{
    levelManager.registerPropertyHandler('lod_distances', (node, value, _level, _gameEngine) =>
    {
        const distancesStr = value as string;

        if(!(node instanceof Mesh))
        {
            return;
        }

        const distances = parseDistances(distancesStr);

        if(distances.length === 0)
        {
            logger.warn(`Invalid lod_distances value: ${ distancesStr }`);
            return;
        }

        const children = node.getChildMeshes(true) as Mesh[];
        const levelCount = Math.min(distances.length, children.length);

        // Add LOD levels for each distance/child pair
        for(let i = 0; i < levelCount; i++)
        {
            node.addLODLevel(distances[i], children[i]);
        }

        // Add null LOD for beyond the last distance (not visible)
        const lastDistance = distances[distances.length - 1];
        node.addLODLevel(lastDistance, null);
    });
}

//----------------------------------------------------------------------------------------------------------------------
